import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowDownRight, ArrowRight, Handshake, Network, Shield } from "lucide-react";
import gsdtLogo from "@/assets/public/gsdt-logo-white.png.asset.json";
import { Shell } from "@/components/public/site";
import { WaveField } from "@/components/public/WaveField";
import { useSiteCopy } from "@/hooks/useSiteCopy";

export const Route = createFileRoute("/gsdt-cluster/")({
  head: () => ({
    meta: [
      { title: "Cluster Membership — Gulf South Defense Tech Cluster" },
      { name: "description", content: "Join the Gulf South Defense Tech Cluster. Maven Scouts invites companies to connect with defense, energy, and industry partners across the Gulf South." },
      { property: "og:title", content: "Cluster Membership — Gulf South Defense Tech Cluster" },
      { property: "og:description", content: "Companies are invited to become members of the Gulf South Defense Tech Cluster." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: Page,
});

const reasons = [
  { n: "01", icon: Network, title: "Connect", body: "Join a network of Gulf South companies working on defense and dual-use technology." },
  { n: "02", icon: Handshake, title: "Collaborate", body: "Meet mission owners, primes, universities, and fellow innovators across the region." },
  { n: "03", icon: Shield, title: "Grow", body: "Get access to opportunities, events, and support to bring your technology to defense customers." },
];

const pillars = [
  { title: "Counter-UAS &\nInstallation Protection", body: "Detection, identification, tracking, sensor fusion, C2, electronic warfare, defeat", anchors: "Barksdale AFB · Fort Johnson · Meta Hyperion · Amazon Caddo–Bossier" },
  { title: "Uncrewed &\nAutonomous Systems", body: "Components, subsystems, sensors, comms, software, payloads: air, ground, maritime", anchors: "Autonomous vessel production · offshore industrial testing" },
  { title: "AI &\nSecure Computing", body: "Applied AI, edge computing, predictive analytics, secure data processing", anchors: "Louisiana AI and data-center corridor · Lumen · Meta Hyperion" },
  { title: "Energy &\nGrid Resilience", body: "Directed energy, microgrids, mobile power, storage, grid monitoring, cooling", anchors: "Weapons Generation Facility · data centers · industrial continuity of operations" },
  { title: "Advanced\nManufacturing", body: "Microelectronics, advanced materials, additive manufacturing, rapid prototyping", anchors: "The reshoring core: returning production and repair to domestic, in-region sources" },
  { title: "Maritime & Critical\nInfrastructure Security", body: "Cybersecurity, OT protection, resilient comms, maritime domain awareness", anchors: "Ports and shipyards · energy corridor · 16 DHS critical infrastructure sectors" },
];

const WHY_SCRIPT = "For dual-use technology companies, the challenge is rarely innovation alone. The real challenge is turning a capable technology into a scalable company that can navigate commercial markets, federal procurement, defense requirements, manufacturing, capital formation, and customer adoption at the same time. The Gulf South Defense Tech Cluster is designed to help Louisiana companies do exactly that. With nearly $260 billion in announced capital investment since 2024, including major commitments from SpaceX, Meta, Amazon, Hyundai Steel, and other global industry leaders, Louisiana is creating new opportunities across defense, energy, advanced manufacturing, infrastructure, cybersecurity, autonomy, AI, sensing, communications, and other strategic sectors. Through Maven Scouts and the MavenWERX commercialization platform, eligible small businesses can access commercialization assessments, customer discovery, a structured 15-week accelerator, federal contracting guidance, manufacturing and supply-chain support, capital-readiness assistance, and connections to potential customers, industry partners, investors, prime contractors, and government organizations.\n\nThe Gulf South Defense Tech Cluster is ultimately about helping companies move from innovation to validation, from validation to contract, and from contract to scale. It will strengthen the connectivity, competitiveness, and performance of Louisiana small businesses within the Defense Industrial Base by creating a coordinated pathway from identification and assessment through customer validation, procurement, production, capital formation, and national growth. By connecting Louisiana’s growing industrial investment, federal demand, research assets, military installations, universities, manufacturers, and entrepreneurial talent, the Cluster will help create more qualified domestic suppliers, awarded contracts, private investment, manufacturing capacity, and high-value jobs. For dual-use founders, the value is simple: build in Louisiana, validate with real customers, access both commercial and defense markets, and position the company to compete nationally.";

function Page() {
  const { getCopy } = useSiteCopy();
  return (
    <Shell>
      {/* Hero */}
      <section className="relative overflow-hidden bg-navy text-navy-foreground">
        <div className="absolute inset-0"><WaveField /></div>
        <div className="relative mx-auto flex min-h-[85vh] max-w-7xl flex-col justify-between px-6 py-16 md:py-20">
          <h1>
            <img src={gsdtLogo.url} alt="Gulf South Defense Tech Cluster" className="w-full max-w-6xl" />
          </h1>
          <div className="mt-20 ml-auto max-w-xl">
            <p className="font-mono text-sm uppercase tracking-[0.25em] text-navy-foreground/80">{getCopy("gsdt_eyebrow", "Cluster Membership")}</p>
            <p className="mt-5 text-3xl leading-tight md:text-4xl">{getCopy("gsdt_sub", "Maven Scouts is inviting companies to participate in the Gulf South Defense Tech Cluster.")}</p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/intake" className="inline-flex items-center gap-4 bg-primary px-6 py-4 font-mono text-xs uppercase tracking-widest text-primary-foreground hover:bg-secondary">
                {getCopy("gsdt_cta", "Become a Member")} <ArrowRight className="h-4 w-4" />
              </Link>
              <a href="#why-join" className="inline-flex items-center gap-4 bg-navy-foreground/15 px-6 py-4 font-mono text-xs uppercase tracking-widest text-navy-foreground hover:bg-navy-foreground/25">
                Why join <ArrowDownRight className="h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Why join script */}
      <section id="why-join" className="scroll-mt-20 bg-background text-foreground">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 py-24 md:grid-cols-[1fr_2fr]">
          <div>
            <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Gulf South Defense Tech Cluster</p>
            <h2 className="mt-4 font-display text-4xl uppercase text-primary">{getCopy("gsdt_why_title", "Why join the cluster")}</h2>
          </div>
          <div>
            <div className="space-y-4 whitespace-pre-line text-lg leading-relaxed">{getCopy("gsdt_why_script", WHY_SCRIPT)}</div>
            <Link to="/intake" className="mt-10 inline-flex items-center gap-4 bg-primary px-6 py-4 font-mono text-xs uppercase tracking-widest text-primary-foreground hover:bg-secondary">
              {getCopy("gsdt_cta", "Become a Member")} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Why join */}
      <section id="why" className="bg-navy text-navy-foreground">
        <div className="mx-auto max-w-7xl border-t border-navy-foreground/15 px-6 py-24">
          <p className="font-mono text-sm uppercase tracking-[0.25em] text-navy-foreground/70">Why participate</p>
          <div className="mt-12 grid gap-px bg-navy-foreground/15 md:grid-cols-3">
            {reasons.map((r) => (
              <div key={r.title} className="bg-navy p-8">
                <div className="flex items-center justify-between font-mono text-xs tracking-widest text-navy-foreground/60">
                  <span>{r.n}</span><r.icon className="h-5 w-5" />
                </div>
                <h2 className="mt-16 font-display text-3xl uppercase">{r.title}</h2>
                <p className="mt-4 text-navy-foreground/75">{r.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="bg-background text-foreground">
        <div className="mx-auto max-w-7xl px-6 py-24">
          <h2 className="border-b border-border pb-4 font-display text-2xl uppercase text-primary md:text-3xl">
            Dual-Use Technology Pillars <span className="mx-2 text-muted-foreground">·</span> <span className="text-lg md:text-xl">Tied to documented military and industrial demand</span>
          </h2>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {pillars.map((p) => (
              <div key={p.title} className="flex flex-col overflow-hidden rounded-md border border-border bg-tint shadow-sm">
                <h3 className="whitespace-pre-line bg-primary px-5 py-3 text-center font-display text-lg uppercase leading-snug text-primary-foreground">{p.title}</h3>
                <div className="flex flex-1 flex-col px-5 py-4">
                  <p className="text-foreground">{p.body}</p>
                  <p className="mt-3 border-t border-border pt-3 text-sm italic text-highlight">{p.anchors}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-navy text-navy-foreground">
        <div className="mx-auto max-w-7xl px-6 pb-24">
          <div className="mt-20 flex flex-col items-start justify-between gap-8 md:flex-row md:items-end">
            <p className="max-w-2xl text-3xl leading-tight md:text-5xl">{getCopy("gsdt_closing", "Build the Gulf South's defense technology base with us.")}</p>
            <Link to="/intake" className="inline-flex shrink-0 items-center gap-4 bg-primary px-6 py-4 font-mono text-xs uppercase tracking-widest text-primary-foreground hover:bg-secondary">
              {getCopy("gsdt_cta", "Become a Member")} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </Shell>
  );
}
